import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp, faThumbsDown } from '@fortawesome/free-regular-svg-icons';
import { submitFeedback, updateFeedbackComment } from '../services/api';
import './FeedbackButton.scss';

/**
 * 피드백 버튼 컴포넌트
 * ChatGPT 스타일의 👍/👎 버튼
 * 
 * @param {string} category - 피드백 카테고리 ('plan', 'screener', 'guide', 'participants')
 * @param {number} studyId - 스터디 ID (선택사항)
 * @param {string} studyName - 스터디 이름 (선택사항)
 */
const FeedbackButton = ({ category, studyId = null, studyName = '' }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submittedVote, setSubmittedVote] = useState(null);
  const [feedbackId, setFeedbackId] = useState(null); // DB에 저장된 피드백 ID

  const handleThumbsUp = async () => {
    if (submitted) return;
    
    setIsSubmitting(true);
    try {
      await submitFeedback(category, true, '', studyId, studyName);
      setSubmitted(true);
      setSubmittedVote(true);
      // "감사합니다!" 메시지 표시
      setTimeout(() => {
        // 자동으로 숨김 (선택사항)
      }, 2000);
    } catch (error) {
      console.error('피드백 제출 실패:', error);
      alert('피드백 제출에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleThumbsDown = async () => {
    if (submitted) return;
    
    // 👎를 처음 누르면 DB에 즉시 저장하고 입력창 표시
    if (!showCommentInput) {
      setIsSubmitting(true);
      try {
        const result = await submitFeedback(category, false, '', studyId, studyName);
        // 피드백 ID 저장 (나중에 코멘트 업데이트용)
        if (result && result.feedback && result.feedback.id) {
          setFeedbackId(result.feedback.id);
        }
        setShowCommentInput(true);
      } catch (error) {
        console.error('피드백 저장 실패:', error);
        alert('피드백 저장에 실패했습니다. 다시 시도해주세요.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleCommentSubmit = async () => {
    setIsSubmitting(true);
    try {
      // feedbackId가 있으면 코멘트만 업데이트, 없으면 그냥 제출 완료 처리
      if (feedbackId) {
        // 코멘트만 업데이트 (이미 DB에 저장된 피드백의 코멘트만 업데이트)
        // 코멘트가 비어있어도 업데이트 가능
        await updateFeedbackComment(feedbackId, comment.trim());
      }
      // 제출 완료 처리 (코멘트가 비어있어도 제출 완료)
      setSubmitted(true);
      setSubmittedVote(false);
      setShowCommentInput(false);
      // "감사합니다!" 메시지 표시
      setTimeout(() => {
        // 자동으로 숨김 (선택사항)
      }, 2000);
    } catch (error) {
      console.error('코멘트 업데이트 실패:', error);
      // 에러가 발생해도 제출 완료 처리 (이미 DB에 저장되어 있으므로)
      setSubmitted(true);
      setSubmittedVote(false);
      setShowCommentInput(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 제출 완료 상태
  if (submitted) {
    return (
      <div className="feedback-button-container">
        <p className="feedback-question">결과물은 어떠셨나요?</p>
        <div className="feedback-thanks">
          감사합니다!
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-button-container">
      {!showCommentInput ? (
        <>
          <p className="feedback-question">결과물은 어떠셨나요?</p>
          <div className="feedback-buttons">
            <button
              className="feedback-btn feedback-btn-up"
              onClick={handleThumbsUp}
              disabled={isSubmitting}
              title="좋아요"
            >
              <FontAwesomeIcon icon={faThumbsUp} />
            </button>
            <button
              className="feedback-btn feedback-btn-down"
              onClick={handleThumbsDown}
              disabled={isSubmitting}
              title="싫어요"
            >
              <FontAwesomeIcon icon={faThumbsDown} />
            </button>
          </div>
        </>
      ) : (
        <div className="feedback-comment-form">
          <p className="feedback-comment-label">어떤 점이 아쉬웠나요? (답변은 더 좋은 서비스를 만드는데 큰 도움이 됩니다)</p>
          <textarea
            className="feedback-comment-input"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="아쉬운 점을 자유롭게 입력해주세요..."
            rows={3}
            disabled={isSubmitting}
          />
          <div className="feedback-comment-actions">
            <button
              className="feedback-comment-submit"
              onClick={handleCommentSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? '제출 중...' : '제출'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackButton;

